import UnreadBadge from './UnreadBadge';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import Button from '../ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../pilot-ui/table';
import { formatTimestamp } from '../../utils/formatTimestamp';
import { useAuth } from '../../context/AuthContext';
import {
  AppUser,
  Note,
  apiMessage,
  category,
  excerpt,
  appUserApi,
  identity,
  noteText,
} from './api';

export default function AppUserPage() {
  const { uid = '' } = useParams();
  const { id: accountId, scopes } = useAuth();
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notesError, setNotesError] = useState('');
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(false);
  const pageRun = useRef(0);

  useEffect(() => {
    if (!scopes.includes('/')) return;
    const run = ++pageRun.current;
    setAppUser(null);
    setNotes([]);
    setCursor(null);
    setError('');
    setNotesError('');
    setPaging(false);
    setLoading(true);
    Promise.all([appUserApi.overview(uid), appUserApi.notes(uid)])
      .then(([account, page]) => {
        if (pageRun.current !== run) return;
        setAppUser(account);
        setNotes(page.items);
        setCursor(page.nextCursor);
      })
      .catch((cause) => {
        if (pageRun.current === run) setError(apiMessage(cause));
      })
      .finally(() => {
        if (pageRun.current === run) setLoading(false);
      });
    return () => {
      pageRun.current++;
    };
  }, [uid, scopes, accountId]);

  async function more() {
    if (!cursor || paging) return;
    const run = pageRun.current;
    setPaging(true);
    setNotesError('');
    try {
      const page = await appUserApi.notes(uid, cursor);
      if (pageRun.current !== run) return;
      setNotes((items) => [...items, ...page.items]);
      setCursor(page.nextCursor);
    } catch (cause) {
      if (pageRun.current === run) setNotesError(apiMessage(cause));
    } finally {
      if (pageRun.current === run) setPaging(false);
    }
  }

  if (!scopes.includes('/'))
    return (
      <main className="mx-auto max-w-5xl p-6">
        <h1 className="text-2xl font-semibold">App user</h1>
        <p role="alert">Viewing App Users requires full device scope (/).</p>
      </main>
    );
  return (
    <main className="mx-auto max-w-6xl space-y-8 p-6">
      <Link
        to="/app-users"
        className="text-primary underline"
        title="Return to app user search and activity"
      >
        ← App Users
      </Link>
      <h1 className="text-2xl font-semibold">
        {appUser ? identity(appUser) : 'App user'}
      </h1>
      {loading && <p role="status">Loading app user…</p>}
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      {appUser && (
        <>
          <section
            aria-labelledby="account-heading"
            className="space-y-3 rounded-lg border border-border bg-card p-4"
          >
            <h2 id="account-heading" className="text-lg font-semibold">
              Account
            </h2>
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt className="text-sm text-muted-foreground">Email</dt>
                <dd>{appUser.email || '—'}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">UID</dt>
                <dd className="break-all">{appUser.uid}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Verified</dt>
                <dd>{appUser.emailVerified ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Created</dt>
                <dd>{formatTimestamp(appUser.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Last sign-in</dt>
                <dd>{formatTimestamp(appUser.lastSignInAt)}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Disabled</dt>
                <dd>{appUser.disabled ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">
                  Contact setting
                </dt>
                <dd>
                  {!appUser.hasUserDoc
                    ? 'Contact unavailable: no app profile'
                    : appUser.notesContactConsentEffective
                      ? 'Allows contact from the team'
                      : 'Turned off contact from the team'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Push</dt>
                <dd>{appUser.pushEnabled === false ? 'Off' : 'On'}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Push token</dt>
                <dd>{appUser.hasPushToken ? 'Present' : 'Not present'}</dd>
              </div>
            </dl>
          </section>
          <section
            aria-labelledby="equipment-heading"
            className="space-y-3 rounded-lg border border-border bg-card p-4"
          >
            <h2 id="equipment-heading" className="text-lg font-semibold">
              Equipment
            </h2>
            {appUser.truncated && (
              <p role="status">Equipment list truncated by the API.</p>
            )}
            {!appUser.locations.length && <p>No equipment found.</p>}
            {appUser.locations.map((home) => (
              <div
                key={home.id}
                className="space-y-3 border-t border-border pt-3"
              >
                <h3 className="font-semibold">Home: {home.name || home.id}</h3>
                {!home.hvacSystems.length && <p>No systems found.</p>}
                {home.hvacSystems.map((system) => (
                  <div key={system.id} className="space-y-2 pl-3">
                    <h4 className="font-medium">
                      System: {system.name || system.id}
                    </h4>
                    {!system.filters.length && <p>No Clips found.</p>}
                    {system.filters.length > 0 && (
                      <h5 className="text-sm font-medium text-muted-foreground">
                        Clips
                      </h5>
                    )}
                    {system.filters.map((clip) => (
                      <div
                        key={clip.id}
                        className="rounded-md border border-border p-3"
                      >
                        <p className="font-medium">
                          {clip.name || clip.location || clip.id}
                        </p>
                        <dl className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                          <div>
                            <dt className="text-muted-foreground">
                              Device number
                            </dt>
                            <dd>{clip.deviceId ?? '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-muted-foreground">Serial</dt>
                            <dd className="break-all">
                              {clip.deviceName || '—'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-muted-foreground">
                              Battery level
                            </dt>
                            <dd>
                              {clip.batteryLevel == null
                                ? '—'
                                : `${clip.batteryLevel}%`}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-muted-foreground">
                              Last connection
                            </dt>
                            <dd>{formatTimestamp(clip.lastConnectionDate)}</dd>
                          </div>
                          <div>
                            <dt className="text-muted-foreground">Connected</dt>
                            <dd>
                              {clip.isConnected == null
                                ? '—'
                                : clip.isConnected
                                  ? 'Yes'
                                  : 'No'}
                            </dd>
                          </div>
                        </dl>
                        {clip.deviceId != null && (
                          <Link
                            className="text-primary underline"
                            to={`/devices/${clip.deviceId}`}
                            title={`Open device ${clip.deviceId} in the console`}
                          >
                            Open device {clip.deviceId}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </section>
          <section
            aria-labelledby="notes-heading"
            className="space-y-3 rounded-lg border border-border bg-card p-4"
          >
            <h2 id="notes-heading" className="text-lg font-semibold">
              Notes
            </h2>
            {notesError && (
              <p role="alert" className="text-destructive">
                {notesError}
              </p>
            )}
            {!notes.length && <p>No notes found.</p>}
            {notes.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Text</TableHead>
                    <TableHead>Latest response</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notes.map((note) => (
                    <TableRow key={note.id}>
                      <TableCell>
                        <Link
                          className="text-primary underline"
                          to={`/app-users/${encodeURIComponent(uid)}/notes/${encodeURIComponent(note.id)}`}
                          title={`Open ${category(note.type)} note and reply thread`}
                        >
                          {category(note.type)}
                        </Link>
                      </TableCell>
                      <TableCell>{formatTimestamp(note.createdAt)}</TableCell>
                      <TableCell className="min-w-48 whitespace-normal">
                        {excerpt(noteText(note))}
                      </TableCell>
                      <TableCell>
                        {note.lastResponse
                          ? `${note.lastResponse.authorType === 'user' ? identity(appUser) : 'MyFluxHive'} · ${formatTimestamp(note.lastResponse.createdAt)}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {note.unread && <UnreadBadge />}
                        {note.needsReply && <span className="ml-2">Needs reply</span>}
                        {!note.unread && !note.needsReply && '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {cursor && (
              <Button
                variant="secondary"
                onClick={more}
                disabled={paging}
                title="Load the next page of this app user's notes"
              >
                {paging ? 'Loading…' : 'Show more'}
              </Button>
            )}
          </section>
        </>
      )}
    </main>
  );
}
